"use client"

import { validationLib } from "@agentcall/core/lib"
import { propertyType } from "@agentcall/core/type"
import { zodResolver } from "@hookform/resolvers/zod"
import { useState } from "react"
import { HelpCircle } from "react-feather"
import { useForm } from "react-hook-form"
import * as z from "zod"
import { useSearchAddressByPostalCode } from "@/api/client/postal-code"
import { EchoesEditGuard } from "@/components/echoes/EchoesEditGuard"
import { Button } from "@/components/ui/button/button"
import { Card, CardContent } from "@/components/ui/card/card"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormRequiredLabel,
} from "@/components/ui/form/form"
import { Input } from "@/components/ui/input/input"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group/radio-group"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select/select"
import { Textarea } from "@/components/ui/textarea/textarea"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip/tooltip"

const currentYear = new Date().getFullYear()
const years = Array.from({ length: currentYear - 1950 + 2 }, (_, i) => currentYear + 1 - i)
const districtPattern = /^[0-9０-９\p{P}\p{S}\s]+$/u
const sanitizeDistrict = (value: string) => value.replace(/[^0-9０-９\p{P}\p{S}\s]/gu, "")

export const propertyFormSchema = z
  .object({
    name: z.string().min(1, "物件名を入力してください"),
    nameKana: z
      .string()
      .min(1, "物件名(カナ)を入力してください")
      .regex(validationLib.KATAKANA_REGEX, validationLib.KATAKANA_ERROR_MESSAGE),
    upperZip: z.string().min(1, "郵便番号（上3桁）を入力してください"),
    lowerZip: z.string().min(1, "郵便番号（下4桁）を入力してください"),
    prefecture: z.string().min(1, "都道府県を入力してください"),
    prefectureKana: z
      .string()
      .min(1, "都道府県(カナ)を入力してください")
      .regex(validationLib.KATAKANA_REGEX, validationLib.KATAKANA_ERROR_MESSAGE),
    city: z.string().min(1, "市区町村を入力してください"),
    cityKana: z
      .string()
      .min(1, "市区町村(カナ)を入力してください")
      .regex(validationLib.KATAKANA_REGEX, validationLib.KATAKANA_ERROR_MESSAGE),
    town: z.string().min(1, "町名を入力してください"),
    townKana: z
      .string()
      .min(1, "町名(カナ)を入力してください")
      .regex(validationLib.ADDRESS_KANA_REGEX, validationLib.ADDRESS_KANA_ERROR_MESSAGE),
    district: z
      .string()
      .min(1, "丁目・番地を入力してください")
      .regex(districtPattern, "丁目・番地は数字・記号のみで入力してください"),
    districtKana: z.string(),
    structure: z.enum(["1", "2", "3", "4"], {
      message: "構造を選択してください",
    }),
    type: z.enum(["1", "2", "3"], {
      message: "用法を選択してください",
    }),
    builtYear: z.number({
      message: "築年を選択してください",
    }),
    floor: z
      .string()
      .min(1, "階数を入力してください")
      .refine(
        (val) => /^\d+$/.test(val) && Number(val) >= 1,
        "階数は1以上の整数で入力してください"
      ),
    ownershipType: z.enum(["WHOLE", "CONDO"], {
      message: "物件の所有形態を選択してください",
    }),
    managementCompanyName: z.string(),
    managementCompanyPhone: z.string(),
    note: z.string(),
  })
  .refine(
    (data) => {
      if (!data.managementCompanyPhone) {
        return true
      }
      return /^\d{10,11}$/.test(data.managementCompanyPhone)
    },
    {
      message: "建物管理会社電話番号は10〜11桁の数字で入力してください",
      path: ["managementCompanyPhone"],
    }
  )

export type PropertyFormValues = z.infer<typeof propertyFormSchema>

export type PropertyFormSubmitValues = Omit<PropertyFormValues, "floor"> & {
  floor: number
}

interface PropertyFormProps {
  defaultValues?: Partial<PropertyFormValues>
  onSubmit: (values: PropertyFormSubmitValues) => Promise<void>
  onCancel: () => void
  onDelete?: () => void
  isEdit?: boolean
  isSubmitting: boolean
}

export function PropertyForm({
  defaultValues,
  onSubmit,
  onCancel,
  onDelete,
  isEdit = false,
  isSubmitting,
}: PropertyFormProps) {
  const [addressSearchError, setAddressSearchError] = useState<string>("")

  const form = useForm<PropertyFormValues>({
    resolver: zodResolver(propertyFormSchema),
    defaultValues: {
      name: "",
      nameKana: "",
      upperZip: "",
      lowerZip: "",
      prefecture: "",
      prefectureKana: "",
      city: "",
      cityKana: "",
      town: "",
      townKana: "",
      district: "",
      districtKana: "",
      builtYear: new Date().getFullYear(),
      structure: undefined,
      type: undefined,
      floor: "",
      ownershipType: "WHOLE",
      managementCompanyName: "",
      managementCompanyPhone: "",
      note: "",
      ...defaultValues,
    },
  })

  const { mutateAsync: searchAddress, isPending: isSearchingAddress } =
    useSearchAddressByPostalCode({
      onSuccess: (result) => {
        if (result) {
          setAddressSearchError("")
          const zip = `${form.getValues("upperZip")}${form.getValues("lowerZip")}`.replace("-", "")
          form.setValue("upperZip", zip.slice(0, 3), { shouldValidate: true })
          form.setValue("lowerZip", zip.slice(3, 7), { shouldValidate: true })
          form.setValue("prefecture", result.prefecture, { shouldValidate: true })
          form.setValue("prefectureKana", result.prefectureKana, { shouldValidate: true })
          form.setValue("city", result.city, { shouldValidate: true })
          form.setValue("cityKana", result.cityKana, { shouldValidate: true })
          form.setValue("town", result.town, { shouldValidate: true })
          form.setValue("townKana", result.townKana, { shouldValidate: true })
          form.setValue("district", result.district, { shouldValidate: true })
          form.setValue("districtKana", "", { shouldValidate: true })
        } else {
          setAddressSearchError("該当する住所が見つかりませんでした")
        }
      },
      onError: () => {
        setAddressSearchError("住所の検索に失敗しました")
      },
    })

  const handleAddressSearch = async () => {
    setAddressSearchError("")
    const zip = `${form.getValues("upperZip")}${form.getValues("lowerZip")}`.replace("-", "")
    if (zip.length !== 7) {
      setAddressSearchError("郵便番号（上3桁と下4桁）を正しく入力してください")
      return
    }

    await searchAddress(zip)
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(async (values) => {
          await onSubmit({
            ...values,
            districtKana: "",
            floor: Number(values.floor),
          })
        })}
        className="space-y-6"
      >
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-semibold flex gap-2 items-center">
                      物件名
                      <FormRequiredLabel />
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="○○サンプルハイツ" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="nameKana"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-semibold flex gap-2 items-center">
                      物件名(カナ)
                      <FormRequiredLabel />
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="○○サンプルハイツ" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="upperZip"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-semibold flex gap-2 items-center">
                        郵便番号
                        <FormRequiredLabel />
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="123"
                          maxLength={3}
                          {...field}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9]/g, "")
                            field.onChange(value)
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="lowerZip"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-semibold flex gap-2 items-center min-h-[1.25rem]" />
                      <FormControl>
                        <Input
                          placeholder="4567"
                          maxLength={4}
                          {...field}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9]/g, "")
                            field.onChange(value)
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="space-y-2">
                <div className="flex gap-3 items-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleAddressSearch}
                    loading={isSearchingAddress}
                    className="text-primary border-slate-400 hover:bg-slate-50"
                  >
                    住所検索
                  </Button>
                  <div className="text-xs text-slate-500">
                    郵便番号を入力して住所を自動入力できます
                  </div>
                </div>
                {addressSearchError && (
                  <p className="text-sm font-medium text-destructive">{addressSearchError}</p>
                )}
              </div>

              <div className="grid grid-cols-10 gap-4">
                <FormField
                  control={form.control}
                  name="prefecture"
                  render={({ field }) => (
                    <FormItem className="col-span-4">
                      <FormLabel className="font-semibold flex gap-2 items-center min-h-[2.5rem]">
                        都道府県
                        <FormRequiredLabel />
                      </FormLabel>
                      <FormControl>
                        <Input placeholder="東京都" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="prefectureKana"
                  render={({ field }) => (
                    <FormItem className="col-span-6">
                      <FormLabel className="font-semibold flex gap-2 items-center min-h-[2.5rem]">
                        都道府県(カナ)
                        <FormRequiredLabel />
                      </FormLabel>
                      <FormControl>
                        <Input placeholder="トウキョウト" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-10 gap-4">
                <FormField
                  control={form.control}
                  name="city"
                  render={({ field }) => (
                    <FormItem className="col-span-4">
                      <FormLabel className="font-semibold flex gap-2 items-center min-h-[2.5rem]">
                        市区町村
                        <FormRequiredLabel />
                      </FormLabel>
                      <FormControl>
                        <Input placeholder="渋谷区" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="cityKana"
                  render={({ field }) => (
                    <FormItem className="col-span-6">
                      <FormLabel className="font-semibold flex gap-2 items-center min-h-[2.5rem]">
                        市区町村(カナ)
                        <FormRequiredLabel />
                      </FormLabel>
                      <FormControl>
                        <Input placeholder="シブヤク" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-10 gap-4">
                <FormField
                  control={form.control}
                  name="town"
                  render={({ field }) => (
                    <FormItem className="col-span-4">
                      <FormLabel className="font-semibold flex gap-2 items-center min-h-[2.5rem]">
                        町名
                        <FormRequiredLabel />
                      </FormLabel>
                      <FormControl>
                        <Input placeholder="恵比寿" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="townKana"
                  render={({ field }) => (
                    <FormItem className="col-span-6">
                      <FormLabel className="font-semibold flex gap-2 items-center min-h-[2.5rem]">
                        町名(カナ)
                        <FormRequiredLabel />
                      </FormLabel>
                      <FormControl>
                        <Input placeholder="エビス" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-10 gap-4">
                <FormField
                  control={form.control}
                  name="district"
                  render={({ field }) => (
                    <FormItem className="sm:col-span-4">
                      <FormLabel className="font-semibold flex gap-2 items-center min-h-[2.5rem]">
                        丁目・番地
                        <FormRequiredLabel />
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="1-2-3"
                          inputMode="numeric"
                          {...field}
                          onChange={(e) => field.onChange(sanitizeDistrict(e.target.value))}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="builtYear"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-semibold flex gap-2 items-center">
                      築年（西暦）
                      <FormRequiredLabel />
                    </FormLabel>
                    <Select
                      onValueChange={(value) => field.onChange(Number(value))}
                      defaultValue={String(field.value)}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="選択してください" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {years.map((year) => (
                          <SelectItem key={year} value={year.toString()}>
                            {year}年
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="structure"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-1">
                      <span className="font-semibold">構造</span>
                      <FormRequiredLabel />
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="h-4 w-4 text-primary cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <div className="max-w-[300px]">
                              <p className="font-bold mb-2">木造</p>
                              <p>「木造」の建物。</p>
                              <p className="font-bold my-2">鉄骨造</p>
                              <p>
                                「軽量鉄骨造」「鉄骨造」「重量鉄骨造」「HPC（鉄骨プレキャストコンクリート造）」「ALC（軽量気泡コンクリート）」の建物。
                              </p>
                              <p className="font-bold my-2">RC造/SRC造/PC造</p>
                              <p>
                                「RC（鉄筋コンクリート造）」「SRC（鉄骨鉄筋コンクリート造）」「PC（プレキャストコンクリート造）」の建物。
                              </p>
                              <p className="font-bold my-2">その他</p>
                              <p>
                                「ブロック」「鉄筋ブロック造」「CFT（コンクリート充鎮鋼管造）」の建物。
                              </p>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="選択してください" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {propertyType.PROPERTY_STRUCTURES.map((structure) => (
                          <SelectItem key={structure.key} value={structure.key}>
                            {structure.value}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-1">
                      <span className="font-semibold">用法</span>
                      <FormRequiredLabel />
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="h-4 w-4 text-primary cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <div className="max-w-[300px]">
                              <p className="font-bold mb-2">用法とは</p>
                              <p>
                                ①専用住宅：住居のみの用途に使用される建物
                                <br />
                                ②共同住宅：1世帯の生活単位となる戸室を2以上有し、各戸室または建物に付属して各世帯が炊事を行う設備がある建物（マンション、アパート等）
                                <br />
                                ③併用住宅：住居の用途と、店舗・事務所等の事業の用途に併用される建物
                              </p>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="選択してください" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {propertyType.PROPERTY_TYPES.map((type) => (
                          <SelectItem key={type.key} value={type.key}>
                            {type.value}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="floor"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-semibold flex gap-2 items-center">
                      階数
                      <FormRequiredLabel />
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="2"
                        type="number"
                        min={1}
                        {...field}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, "")
                          field.onChange(value)
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="ownershipType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-semibold flex gap-2 items-center">
                      物件の所有形態
                      <FormRequiredLabel />
                    </FormLabel>
                    <FormControl>
                      <RadioGroup
                        onValueChange={field.onChange}
                        value={field.value}
                        className="flex flex-col space-y-2 py-2"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="WHOLE" id="ownership-whole" />
                          <label
                            htmlFor="ownership-whole"
                            className="cursor-pointer text-sm font-medium leading-none"
                          >
                            一棟所有物件
                          </label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="CONDO" id="ownership-condo" />
                          <label
                            htmlFor="ownership-condo"
                            className="cursor-pointer text-sm font-medium leading-none"
                          >
                            区分所有物件（分譲マンション等）
                          </label>
                        </div>
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="managementCompanyName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-1">
                      建物管理会社名
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="h-4 w-4 text-primary cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="max-w-[300px]">
                              建物の管理を委託している管理会社がある場合は、その名称をご入力ください。
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="○○サンプル株式会社" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="managementCompanyPhone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-semibold flex gap-2 items-center">
                      建物管理会社電話番号（ハイフンなし）
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="01201234567"
                        maxLength={11}
                        {...field}
                        onChange={(e) => {
                          // 数字のみを許可
                          const value = e.target.value.replace(/[^0-9]/g, "")
                          field.onChange(value)
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="note"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-semibold flex gap-2 items-center">備考</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="南向きで日当たり良好。朝は気持ちの良い光で目覚められます。"
                        className="min-h-[100px]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-3 justify-end">
          {isEdit && (
            <div className="flex-1">
              <EchoesEditGuard>
                <Button type="button" variant="destructive" onClick={() => onDelete?.()}>
                  物件削除
                </Button>
              </EchoesEditGuard>
            </div>
          )}
          <Button type="button" variant="outline" onClick={onCancel}>
            キャンセル
          </Button>
          <EchoesEditGuard>
            <Button type="submit" disabled={isSubmitting}>
              {isEdit ? "更新する" : "登録する"}
            </Button>
          </EchoesEditGuard>
        </div>
      </form>
    </Form>
  )
}
